import React from 'react';

export const Footer = ({
  companyName = "Real Patients",
  tagline = "Your story can transform healthcare.",
  backgroundColor = "#272726",
  copyrightYear = "2026",
  // Navigation handlers
  onHomeClick,
  onSignUpClick,
  onAboutClick,
  onTrustCenterClick,
  onPatientJourneysClick,
  onPartnersClick,
  onOurStoryClick,
  onRequestProjectClick,
  onCommunityClick,
  onConditionClick,
  onQuizClick,
  onPrivacyClick,
  onTermsClick,
  conditions = [],
  contactEmail = "hello@realpatients.com",
  linkedinUrl = "https://linkedin.com/company/realpatients",
  twitterUrl = "https://twitter.com/realpatients",
  instagramUrl = "https://instagram.com/realpatients"
}) => {
  const linkStyle = {
    color: 'rgba(255,255,255,0.6)',
    textDecoration: 'none',
    fontSize: '14px',
    transition: 'color 0.2s',
    cursor: 'pointer',
    display: 'block',
    marginBottom: '14px',
    textAlign: 'center'
  };

  const headingStyle = {
    fontSize: '13px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.6)',
    margin: '0 0 20px 0',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    textAlign: 'center'
  };

  const handleHover = (e, isEnter) => {
    e.target.style.color = isEnter ? '#ffffff' : 'rgba(255,255,255,0.6)';
  };

  return (
    <footer
      style={{
        backgroundColor: backgroundColor,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        position: 'relative'
      }}
    >
      {/* Organic wave transition from light to dark */}
      <div style={{ position: 'relative', height: '120px', background: '#f7f5f3' }}>
        <svg
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '100%'
          }}
        >
          <defs>
            <linearGradient id="footerFade" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#f7f5f3" />
              <stop offset="40%" stopColor="#a8a8a6" />
              <stop offset="100%" stopColor="#272726" />
            </linearGradient>
          </defs>
          <path
            d="M0,40 C240,80 480,10 720,50 C960,90 1200,20 1440,60 L1440,120 L0,120 Z"
            fill="url(#footerFade)"
          />
          <path
            d="M0,60 C180,30 400,90 640,50 C880,10 1100,70 1440,40 L1440,120 L0,120 Z"
            fill="#272726"
            opacity="0.5"
          />
          <path
            d="M0,80 C300,50 600,100 900,70 C1150,45 1300,85 1440,60 L1440,120 L0,120 Z"
            fill="#272726"
          />
        </svg>
      </div>
      <div style={{ padding: '40px 24px 0' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Mobile styles */}
        <style>{`
          @media (max-width: 768px) {
            .footer-grid {
              grid-template-columns: 1fr 1fr !important;
              gap: 40px 24px !important;
            }
          }
          @media (max-width: 480px) {
            .footer-grid {
              grid-template-columns: 1fr !important;
              gap: 32px !important;
            }
          }
        `}</style>

        {/* Main footer content */}
        <div
          className="footer-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
            gap: '48px',
            paddingBottom: '48px',
            borderBottom: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          {/* Tagline column */}
          <div style={{ textAlign: 'center' }}>
            <button
              onClick={onHomeClick}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                marginBottom: '16px',
                display: 'inline-block',
              }}
            >
              <h3
                style={{
                  fontFamily: '"Source Serif Pro", Georgia, serif',
                  fontSize: '24px',
                  fontWeight: '600',
                  color: '#ffffff',
                  margin: 0,
                }}
              >
                {companyName}
              </h3>
            </button>
            <p
              style={{
                fontFamily: '"Source Serif Pro", Georgia, serif',
                fontSize: '18px',
                color: 'rgba(255,255,255,0.9)',
                margin: '0 auto 32px',
                lineHeight: '1.5',
                maxWidth: '260px',
              }}
            >
              {tagline}
            </p>
          </div>

          {/* Company column */}
          <div>
            <h4 style={headingStyle}>Company</h4>
            <nav>
              <button
                onClick={onOurStoryClick}
                style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                onMouseEnter={(e) => handleHover(e, true)}
                onMouseLeave={(e) => handleHover(e, false)}
              >
                Our Story
              </button>
              {onPartnersClick && (
                <button
                  onClick={onPartnersClick}
                  style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                  onMouseEnter={(e) => handleHover(e, true)}
                  onMouseLeave={(e) => handleHover(e, false)}
                >
                  Companies/Partners
                </button>
              )}
              {onRequestProjectClick && (
                <button
                  onClick={onRequestProjectClick}
                  style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                  onMouseEnter={(e) => handleHover(e, true)}
                  onMouseLeave={(e) => handleHover(e, false)}
                >
                  Request a Project
                </button>
              )}
              {conditions.map((condition) => (
                <button
                  key={condition.name}
                  onClick={() => onConditionClick && onConditionClick(condition.name)}
                  style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                  onMouseEnter={(e) => handleHover(e, true)}
                  onMouseLeave={(e) => handleHover(e, false)}
                >
                  {condition.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Get Involved column */}
          <div>
            <h4 style={headingStyle}>Get Involved</h4>
            <nav>
              <button
                onClick={onSignUpClick}
                style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                onMouseEnter={(e) => handleHover(e, true)}
                onMouseLeave={(e) => handleHover(e, false)}
              >
                Sign Up
              </button>
              {onCommunityClick && (
                <button
                  onClick={onCommunityClick}
                  style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                  onMouseEnter={(e) => handleHover(e, true)}
                  onMouseLeave={(e) => handleHover(e, false)}
                >
                  Join the Conversation
                </button>
              )}
              <a
                href={`mailto:${contactEmail}`}
                style={linkStyle}
                onMouseEnter={(e) => handleHover(e, true)}
                onMouseLeave={(e) => handleHover(e, false)}
              >
                Contact Us
              </a>
            </nav>
          </div>

          {/* Legal column */}
          <div>
            <h4 style={headingStyle}>Legal</h4>
            <nav>
              <button
                onClick={onPrivacyClick}
                style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                onMouseEnter={(e) => handleHover(e, true)}
                onMouseLeave={(e) => handleHover(e, false)}
              >
                Privacy Policy
              </button>
              <button
                onClick={onTermsClick}
                style={{ ...linkStyle, background: 'none', border: 'none', padding: 0, width: '100%' }}
                onMouseEnter={(e) => handleHover(e, true)}
                onMouseLeave={(e) => handleHover(e, false)}
              >
                Terms of Service
              </button>
            </nav>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            paddingTop: '24px',
            paddingBottom: '32px',
            borderTop: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <p
              style={{
                fontSize: '13px',
                color: 'rgba(255,255,255,0.4)',
                margin: '0'
              }}
            >
              © {copyrightYear} {companyName}
            </p>
            <p
              style={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.25)',
                margin: '0'
              }}
            >
              Formerly Savvy Cooperative · Est. 2016
            </p>
          </div>
          <p
            style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.3)',
              margin: '0'
            }}
          >
            Made with care for patients, by patients.
          </p>
        </div>
      </div>
      </div>
    </footer>
  );
};
